import { ModalShell } from '../design/ModalShell';
import { WizardJourney } from '../design/WizardJourney';
import { ReviewPreviewCard } from '../design/ReviewPreviewCard';
import { FormField } from '../design/FormField';
import { ReadbackField } from '../design/ReadbackField';
import { Button } from '../design/Button';
import { ScrollColumn } from '../design/ScrollColumn';
import type { ScrollMetrics } from '../design/ScrollColumn';
import { refLabel } from './vmAdapters';
import { RESPONSIBILITY_STATEMENT } from '../ReviewStage';
import { GuardrailPanel, type ArtefactGuardState } from '../GuardrailPanel';
import Kombi from '../../../design/Kombi';
import type { UploadOverlayVm } from '../useUploadOverlay';

/**
 * Tool Upload P4 V2 — "Review your writeup" (wizard step 3)
 *
 * The third numbered wizard step (`activeStep=3`, pill 3 drawn @0.6). Its CHROME,
 * MODAL and STEP-PILL row are identical to P2/P3, but the BODY is a DIFFERENT
 * construction and does NOT reuse the `grid-cols-wizard-body` skeleton:
 *
 *   - LEFT: one LARGE preview card (445x395), not the P2/P3 identity + small
 *     preview stack. See ReviewPreviewCard — a distinct component.
 *   - RIGHT: a read-back of the P2/P3 form, under two section headers "Details"
 *     and "Instructions". It SCROLLS — the content is taller than the viewport.
 *   - The line down the middle is that column's SCROLLBAR (see below), not a
 *     divider; the footer rule is genuinely FULL-WIDTH.
 *
 * MEASURED from `Tool Upload P4 V2.svg`. The modal is the shared true box
 * 190,231 1056x640 r16 (collapsing 190.5,231.5 1055x639 r15.5). Coordinates
 * below are CANVAS; modal-local is canvas − (190,231).
 *
 * THE THREE RULES, and how P4 diverges from P2/P3:
 *
 *   header    191,328.5 -> 1245,328.5   full width   @1   — same as P2/P3
 *   scrollbar 693.5,361 -> 693.5,756    TWO-TONE           — see below
 *   footer    190,788.5 -> 1245,788.5   FULL WIDTH   @1   — NOT right-column-only
 *
 * THE MIDDLE LINE IS A SCROLLBAR, NOT A DIVIDER. The source draws it as TWO
 * stacked `<line>`s at the same x=693.5: a faint `stroke=#022B30 @0.2` running
 * the full 361..756 (395px — the TRACK), and a SOLID `@1` segment over just its
 * top 33px (361..394 — the THUMB, at the top). A 33px thumb in a 395px track is
 * ~8% visible, i.e. the right column's content is ~12x the viewport: the review
 * has a lot below the fold, and the frame is captured scrolled to the top. So
 * this is a live scroll indicator for the right column, reproduced by ScrollColumn:
 * the right column is a 395px viewport that scrolls its overflowing content, and
 * the two-tone bar is that scroller's custom scrollbar — faint track, solid thumb
 * whose height reflects viewport/content and whose offset tracks scrollTop. The
 * thumb is a child pinned within its track (a child-within-parent pin, sanctioned
 * — never a page coordinate). The track runs 361..756 (32.5 below the header rule
 * to the preview card's bottom), NOT the full body height and NOT to the footer.
 *
 * THE FOOTER RULE IS FULL-WIDTH (190..1245), unlike P2/P3's right-column-only
 * 486..1245. P4 can afford this: the preview card ends at canvas y=756 and the
 * footer rule is at 788.5, a 32.5px clearance, so a full-width band clips nothing
 * on the left (the P2/P3 clip came from the left panel running to the modal
 * floor — P4's left column ends well above the footer). The footer is therefore a
 * FULL-WIDTH group at the bottom of the body, spanning both columns, pushed down
 * with mt-auto — flow, not a coordinate, and NOT ModalShell's footer slot.
 *
 * LEFT PREVIEW CARD, canvas (223,361) 445x395 — see ReviewPreviewCard for the
 * plate/tile/text geometry. It is ONE specimen with a fixed size, so it does not
 * flex; its column does.
 *
 * RIGHT READ-BACK FORM, fields at canvas x=717.5 (23.5 in from the rule at 694),
 * width 495. All boxes r7.5 stroke #022B30 @0.2, EMPTY (the frame draws no
 * values — same as P2/P3), so FormField renders its own empty inputs; no
 * `children` read-back values are passed because the design shows none.
 *
 *   "Details" header    717,373.5   11/500
 *   Tool Name*          717.5,419.5   245 x 31   |  Primary Category* 985.5,419.5  227 x 31
 *     -> two-up, 245:227, gap 23 (985.5 - 962.5). derive closure 245+23+227=495 PASS.
 *   Tool Description*   717.5,490.5   495 x 71   (tall)
 *   Tags*               717.5,601.5   495 x 31
 *   Time Saved*         717.5,672.5   245 x 31   (left track; right half empty)
 *   "Instructions" header  717,732.5   11/500   — its fields are BELOW THE FOLD
 *
 * Q4 REVISITED — THE INSTRUCTIONS FIELDS ARE BELOW THE FOLD, NOT ABSENT. The
 * earlier reading ("Instructions header with no fields") mistook the scroll
 * viewport's bottom edge for the end of the form. The middle line is a scrollbar
 * whose thumb sits at the top at ~8%, which means most of the column is off-screen
 * downward: the "Instructions" header is the LAST thing visible at the top of the
 * scroll, and its fields — the four action instructions the user filled on P3 —
 * live below the fold. So they ARE rendered here (read back from P3's action list,
 * `toolActions`), and it is that overflow which makes the scrollbar real. The
 * frame does not draw them because the frame is scrolled to the top.
 *
 * FIELD RHYTHM is the same as P2: --label-field-gap (6) label->field via
 * FormField's own gap-field-gap, --field-row-gap (14) field->next-label via
 * gap-row-gap (NOT gap-field-row, which is unregistered -> gap 0, the P3 bug).
 * The two SECTION HEADERS carry their own measured gaps: "Details" sits 30 below
 * the header rule (--wizard-rule-gap-form, the same rule-to-first-label gap the
 * form uses), and "Instructions" sits below Time Saved with the row gap.
 *
 * FOOTER — Cancel + Next only. P4 has NO help line (unlike P2/P3) and NO
 * "Continue Draft" (P1 only). Byte-identical controls to P2's footer:
 *   Cancel  1094.5,815.5  59x22  r7.5  stroke #022B30 @0.3
 *   Next    1168.5,815.5  48x22  r7.5  stroke #022B30 @0.3
 *     -> gap 15 (--modal-footer), right inset 29 (--modal-footer-r).
 *
 * WHAT CHANGED IN THE BACKEND COPY. Layout, classNames and every measurement above
 * are untouched. Only data and handlers — and this page's own TODO(dev) (just above,
 * in the Design's words: "wire all of them to the wizard's collected state (read-only
 * on this review step)") is what got done:
 *
 *   1. `uploadedTools[0]` / `toolActions` fixture reads -> the live Vm. The preview
 *      card's name/owner come from the draft and the signed-in builder; the
 *      Instructions rows come from `vm.components` + `vm.explanations`, so the row
 *      count follows the automation rather than the fixture's four.
 *   2. THE BOXES NOW CARRY VALUES, via FormField's own `children` slot and
 *      `ReadbackField` — which wears FormField's input classes verbatim, so a
 *      read-back row is pixel-identical to the editable row it mirrors. The frame
 *      draws them empty only because it is a capture of an unfilled form. They are
 *      READ-ONLY: this is a review, and the editable copy of each field is two steps
 *      back. See ReadbackField's header for why not `<FormField value=…/>`.
 *   3. THREE FIELDS ARE FOREIGN KEYS. Category / Access / Department are numeric ids
 *      in the draft, so the read-back resolves each id to its `refData` label
 *      (`refLabel`) — a bare `7` in a review screen reads as data, and it is not.
 *   4. FIVE MORE DETAILS ROWS. The frame draws five (Tool Name, Primary Category,
 *      Description, Tags, Time Saved) but the backend collects ten, and a review that
 *      hides half of what is about to be published is not a review. The five added
 *      rows (Problem Solved, Access, Department, Dependencies, Requirements) use the
 *      same measured boxes and the same rhythm — this is the "add more boxes as
 *      necessary" the brief allows, in the drawn style, inventing no new geometry.
 *      Requirements is omitted entirely when `vm.requirementsRequired` is false: it is
 *      not collected in that case, so an em-dash row would be reporting an absence
 *      that is not one.
 *   5. `navigate(…)` -> `vm.goToStage('instructions')` for Back and `vm.onUpload` for
 *      Next. Next is the real upload, not a navigation — P5 is reached by the upload
 *      SUCCEEDING (`phase.kind === 'done'`), which is why it is a phase and not a
 *      stage. It gates: an unticked attestation keeps the builder here.
 *   6. THE ATTESTATION TICK, added IN the footer row — left of Back/Next, below the
 *      rule (the designer's placement; it first went in above the rule as its own
 *      full-width block). The backend refuses to upload until `draft.attested` is true
 *      (useUploadOverlay.ts:370, which deliberately stays on Review because "the
 *      message is beside the tick, which is where the builder is looking") — so
 *      without a tick the drawn Next button would be dead with nothing on screen
 *      explaining why, and sharing the row with Next puts the reason in the same
 *      glance as the button it gates. `onAttestChange` already existed for it.
 *
 * TODO(dev), unchanged from the Design: the preview tile is a placeholder thumbnail
 * (see ReviewPreviewCard) — the real thumbnail is not carried through this flow.
 */
/**
 * The two-tone scrollbar for the right column, handed to ScrollColumn and given
 * the live scroll metrics. The track is h-review-card (395 — the drawn track
 * height) and w-px, bg-rule-faint (#022B30 @0.2, the exact faint paint the source
 * draws). The thumb is bg-ink (@1, the solid segment), sized and positioned from
 * the metrics:
 *
 *   height = thumbFraction (viewport/content) of the track
 *   top    = offsetFraction (scrollTop/overflow) of the FREE track (track − thumb)
 *
 * expressed as percentages so both resolve against the track's own 395px height —
 * no page coordinate, and the thumb is a child pinned within its track (the
 * sanctioned child-in-parent pin). At rest (scrollTop 0) top is 0 and the thumb
 * sits at the top exactly as the frame draws it. No transition — the thumb is
 * direct scroll feedback like a native bar, so reduced-motion has nothing to
 * suppress. Height/top only (compositor-cheap enough; not animated).
 *
 * When the content does not overflow (scrollable false) the thumb fills the track
 * and the bar reads as a plain full-height rule, which is the correct degenerate
 * state — nothing to scroll.
 */
function renderScrollbar(metrics: ScrollMetrics) {
  const thumbPct = `${(metrics.thumbFraction * 100).toFixed(4)}%`;
  // Offset along the free track: offsetFraction of (100% − thumbHeight).
  const topPct = `calc(${(metrics.offsetFraction * 100).toFixed(4)}% * ${(
    1 - metrics.thumbFraction
  ).toFixed(6)})`;

  return (
    <div className="pt-wizard-rule-gap-form" aria-hidden="true">
      <div className="relative h-review-card w-px bg-rule-faint">
        <div className="absolute inset-x-0 bg-ink" style={{ height: thumbPct, top: topPct }} />
      </div>
    </div>
  );
}

export function UploadReviewPage({
  vm,
  guardArtefact,
}: {
  vm: UploadOverlayVm;
  /**
   * UP-F19 (GC-6/GC-8): the artefact-side guardrail facts, supplied by C1's artefact wizard branch
   * when this review renders for a File/Prompt/Link upload. Omitted on the Dataverse-flow review (this
   * page's default caller, UploadWizard), where there is no artefact captured through this panel — so
   * the two artefact rows show PENDING rather than a false negative (the spec's "cannot yet be
   * evaluated → pending"). Every other guardrail is derived from the shared wizard state below.
   */
  guardArtefact?: ArtefactGuardState;
}) {
  const { details, components, explanations, draft } = vm;
  const { refData } = vm.props;

  // The attestation gate, made visible. `onUpload` refuses to upload with an
  // unticked box and stays on Review (useUploadOverlay:370), but did so silently —
  // a dead Next with nothing explaining why. Once the builder has TRIED to advance
  // (triedStage.review), an unticked box reddens its border and the confirm copy,
  // the same red the fields use. Ticking it clears the state (it becomes valid).
  const attested = draft?.attested ?? false;
  const attestInvalid = vm.triedStage.review && !attested;
  // A SECOND, NON-BLOCKING choice. Unticked, the tool is held for the AI SWAT team to verify before it
  // goes live; ticked, the builder vouches for it and it publishes straight to Draft. Default unticked
  // (the safe route). Deliberately NOT part of the red-validation gate — it never blocks Next.
  const takesResponsibility = draft?.takesResponsibility ?? false;

  // UP-F45: the submit button's IDLE label reflects the platform moderation posture, so it tells the
  // builder where this submission will land BEFORE they press it. self_moderated publishes straight
  // to the catalogue ('Publish'); manual_review enters the reviewer queue ('Submit for review'). While
  // a request is in flight the label is the mode's in-progress form. This only relabels — it never
  // gates; the outstanding-items hint below stays informative and the button stays enabled.
  const publishes = vm.moderationMode === 'self_moderated';
  const submitLabel = vm.submitting
    ? publishes
      ? 'Publishing…'
      : 'Submitting…'
    : publishes
      ? 'Publish'
      : 'Submit for review';
  // The pre-press hint (UP-F45). Derived entirely from existing hook state (detailErrors /
  // instructionsComplete / attested via useUploadOverlay's outstandingItems). Surfaced only once the
  // builder has TRIED to submit, matching the attestation tick's reveal-on-demand — showing it up
  // front would nag before they have engaged. The button is NOT disabled by it: clicking with items
  // outstanding runs onUpload, which focuses the first offender.
  const outstanding = vm.triedStage.review ? vm.outstandingItems : [];

  return (
    <ModalShell
      label="Review your writeup"
      headerInset="pills"
      // Like P2/P3: this is a two-column step whose left preview rail and footer
      // stay put while only the form column scrolls, so the BODY must not be the
      // scroller. Without this the body is overflow-y-auto with an indefinite
      // height, the child's h-full resolves to auto, the grid grows past the 639
      // frame and the footer is pushed below the visible box (ModalShell's own
      // bodyScroll note). This was the missing piece — the fields overflowed the
      // modal because P4 alone omitted it.
      bodyScroll={false}
      header={
        // The step pills, then the full-width header rule beneath them —
        // identical to P2/P3. The header region is inset by --step-row-inset
        // (65), so the rule is -mx'd back out by that amount to span 191..1245.
        <>
          <WizardJourney activeStep={3} />
          <div className="-mx-step-row mt-11 h-px bg-ink" />
        </>
      }
    >
      {/* The body: preview column | 1px two-tone column rule | form column,
          then a FULL-WIDTH footer group spanning both, pushed to the modal
          floor with mt-auto. h-full so the body fills the modal interior below
          the header.

          The outer flex-col holds the two-column grid and the footer; the grid
          takes its natural height and the footer's mt-auto consumes the slack —
          the same flow-not-coordinate footer discipline as P2/P3, but here the
          footer is full-width (see the header note) rather than right-column. */}
      <div className="flex h-full min-w-0 flex-col">
        {/* grid-cols-review-body: preview-fr | 1px rule | form-fr, derived and
            closure-checked (471.5 + 1 + 519.5 = 992). The middle track IS the
            faint full-height column rule; the solid top cap is pinned to that
            track's top. NO shared pt- on the grid: the preview card and the
            "Details" header start at different heights (the card's column top vs
            the form's 30px rule gap), so each column carries its own top gap. */}
        {/* items-start, NOT the grid default (stretch): the viewport column
            declares its own height (h-review-card, 395) and must clip its overflow.
            Under stretch the row grows to the tallest item (the tall form stack)
            and stretches every column to match, overriding the viewport's height so
            it never clips. Aligning items to the top lets each column keep its own
            height — preview card and viewport both 395. */}
        <div className="grid grid-cols-review-body items-start">
          {/* LEFT PREVIEW COLUMN. The card starts at the column's top; the
              column flexes while the card stays 445 wide. pt matches the card's
              canvas top (361) relative to the header band; it shares the form's
              --wizard-rule-gap-form (30) rule-to-content gap so both columns
              begin level with the two-tone rule's start. */}
          <div className="flex min-w-0 flex-col pt-wizard-rule-gap-form">
            {/* The fixture's first uploaded tool -> the tool being uploaded. The
                name falls back to the automation's own name while the field is
                blank, matching P2/P3's preview. */}
            <ReviewPreviewCard
              name={details.name || vm.props.flow.name}
              owner={vm.props.builderName}
              // The 415x272 plate is the largest box the flow is ever drawn in, at
              // roughly twice the wizard plate's detail budget. By this step the
              // manifest has loaded, so this is the structural diagram.
              shape={vm.components}
            />
          </div>

          {/* SCROLLBAR + SCROLLING FORM COLUMN, as ONE ScrollColumn. It renders
              two siblings — the scrollbar (into the grid's 1px middle track) and
              the viewport (into the form track) — linked by a ref, so the layout
              stays in flow: the two are grid items 2 and 3, not overlaid boxes.

              The viewport is exactly h-review-card (395) — the same height as the
              scrollbar track, so their bottoms line up. The 30px rule gap that sets
              the first line level with the track top (canvas 361) is NOT the
              viewport's own padding (that would ride inside the 395 and shorten the
              scroll region, and the matching 30px bottom pad would then let you
              scroll into empty space). Instead the inner content stack carries the
              gap symmetrically — pt- and pb-wizard-rule-gap-form on the child — so
              the first field sits 30 below the top and the last 30 above the bottom,
              both INSIDE the 395 scroll region. Content overflows (Details + the
              Instructions rows), which is what makes the thumb short, as the frame
              draws it. */}
          <ScrollColumn
            viewportClassName="h-review-card pl-review-form mt-[30px]"
            scrollbar={renderScrollbar}
          >
            <div className="flex min-w-0 flex-col gap-row-gap pt-0 pb-0">
              {/* "Details" section header — 11/500, same as a field label. */}
              <h3 className="text-xs font-medium text-ink">Details</h3>

              {/* Two-up row: 245:227, not 1fr 1fr. See grid-cols-form-review.
                  RESPONSIVE — single-column BELOW account (760), two-up only
                  AT >=760, the same crossover P2's 343:318 row uses (the same
                  construct in the same wizard column). Stacked, the two fields
                  keep the 14px field rhythm (gap-y-row-gap); the 23px column
                  gap only applies once two-up. */}
              <div className="grid grid-cols-1 gap-y-row-gap account:grid-cols-form-review account:gap-x-col-gap-narrow account:gap-y-0">
                <FormField label="Tool Name*" name="tool-name" required>
                  <ReadbackField value={details.name} />
                </FormField>
                {/* A numeric FK in the draft — resolved to its refData label. */}
                <FormField label="Primary Category*" name="primary-category" required>
                  <ReadbackField value={refLabel(refData.categories, details.categoryId)} />
                </FormField>
              </div>

              <FormField label="Tool Description*" name="tool-description" size="tall" required>
                <ReadbackField value={details.description} size="tall" />
              </FormField>

              {/* ADDED (note 4): collected on P2, published with the tool. */}
              <FormField label="Problem Solved*" name="problem-solved" size="tall" required>
                <ReadbackField value={details.problemSolved} size="tall" />
              </FormField>

              {/* Tags are a string[] in the draft; the chip input's own separator
                  is a comma, so they read back the way they were entered. */}
              <FormField label="Tags*" name="tags" required>
                <ReadbackField value={details.tags.join(', ')} />
              </FormField>

              {/* ADDED (note 4): the other two FKs, two-up in the same measured
                  245:227 tracks the Name/Category row uses. */}
              <div className="grid grid-cols-1 gap-y-row-gap account:grid-cols-form-review account:gap-x-col-gap-narrow account:gap-y-0">
                <FormField label="Access Requirement*" name="access-requirement" required>
                  <ReadbackField value={refLabel(refData.accessRequirements, details.accessId)} />
                </FormField>
                <FormField label="Department*" name="department" required>
                  <ReadbackField value={refLabel(refData.departments, details.departmentId)} />
                </FormField>
              </div>

              {/* Time Saved* is 245 wide — the left track — with the right half
                  empty in the source. Same two-track grid with an empty second
                  cell as P2's Time Saved, so the field keeps its measured
                  proportion. Single-column below account. */}
              <div className="grid grid-cols-1 account:grid-cols-form-review account:gap-x-col-gap-narrow">
                <FormField label="Time Saved*" name="time-saved" required>
                  <ReadbackField value={details.estimatedTimeSaved} />
                </FormField>
              </div>

              {/* ADDED (note 4). */}
              <FormField label="Dependencies*" name="dependencies" size="tall" required>
                <ReadbackField value={details.dependencies} size="tall" />
              </FormField>

              {/* ADDED (note 4), and OMITTED when the backend does not collect it:
                  `requirementsRequired` is false for a flow that needs none, and an
                  em-dash row would report an absence that is not one. */}
              {vm.requirementsRequired ? (
                <FormField label="Requirements*" name="requirements" size="tall" required>
                  <ReadbackField value={details.requirements} size="tall" />
                </FormField>
              ) : null}

              {/* "Instructions" section header — the last thing visible at the
                  top of the scroll. Its fields (the four P3 action instructions)
                  are BELOW THE FOLD; that overflow is what makes the scrollbar
                  real. Read back from toolActions — the SAME action list P3
                  draws (Outlook/Gmail/Click/Save), labels verbatim. The frame
                  draws no VALUE for any instruction (empty on P3 too), so these
                  read-back rows show FormField's own empty box, size="action"
                  like P3's originals. TODO(dev): bind each to the value the user
                  typed on P3 (read-only here). */}
              <h3 className="text-xs font-medium text-ink">Instructions</h3>
              {components.map((component) => (
                <FormField
                  key={component.id}
                  // The same composed label P3 writes, so the review row and the
                  // row it mirrors read identically: hierarchical number, the
                  // author's own step name, and the branch where there is one.
                  label={`${component.numbering} — ${
                    component.branch ? `${component.branch} — ` : ''
                  }${component.label}*`}
                  name={`review-${component.id}`}
                  size="action"
                  required
                >
                  <ReadbackField value={explanations[component.id] ?? ''} size="action" />
                </FormField>
              ))}

              {/* UP-F19 GUARDRAIL PANEL — the nine platform guardrails GC-1..GC-9 as a live checklist,
                  ticking/warning/pending as the builder fills the wizard. It sits in the scrolling
                  read-back so the builder sees, at the point of submitting, exactly which guardrails
                  are met and which are still outstanding. Every row is derived from state this page
                  already reads (vm.detailErrors, draft.attested) plus the artefact facts C1's wizard
                  passes for the File/Prompt/Link case; on the Dataverse-flow review no artefact prop is
                  passed, so GC-6/GC-8 read pending. Informative only — it never gates Next (UP-F45). */}
              <GuardrailPanel
                inputs={{
                  detailErrors: vm.detailErrors,
                  attested,
                  ...(guardArtefact ? { artefact: guardArtefact } : {}),
                }}
              />

              {/* VERIFICATION — the non-blocking "take responsibility" tick. It lives here in the
                  scrolling read-back rather than the footer BECAUSE it does not gate Next (unlike the
                  attestation tick, which shares Next's row so its reason is in the same glance). Leave
                  it unticked and the AI SWAT team verifies the tool before it is published; tick it to
                  vouch for the tool yourself and publish as a Draft straight away. */}
              <h3 className="text-xs font-medium text-ink">Verification</h3>
              <label className="flex min-w-0 items-start gap-3 text-xs font-light text-ink">
                <input
                  type="checkbox"
                  checked={takesResponsibility}
                  onChange={(e) => vm.onResponsibilityChange(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm border border-border-subtle accent-accent-link outline-none focus-visible:border-ring-focus"
                />
                <span className="min-w-0">{RESPONSIBILITY_STATEMENT}</span>
              </label>
            </div>
          </ScrollColumn>
        </div>

        {/* FULL-WIDTH FOOTER GROUP — spans both columns, pinned to the modal
            floor by flow (mt-auto), not a coordinate. Unlike P2/P3 the rule is
            genuinely full width (canvas 190..1245): -mx-modal-x escapes
            ModalShell's --modal-pad-x body padding on both sides to reach the
            modal's inner edges. Then ONE row below it: the attestation tick on the
            left, Back + Next right-aligned (no help line, no Continue Draft). */}
        <div className="mt-auto pb-wizard-footer-b">
          {/* THE UPLOAD ERROR LINE. `onUpload` is the real POST, and its failure
              paths (expired session, Dataverse permission, a 4xx/5xx from the API)
              set vm.errorMsg and — for the ones that keep the builder here — return
              on Review. Without this line those returns look identical to a dead
              button: Next "does nothing", P5 never appears. The client-side gates
              redden the offending field/tick instead of using this; what surfaces
              here is the SERVER's reason, which no field colour can express. Sits
              just above the footer rule, in the builder's eyeline for Next, in the
              same error red. role=alert so it is announced when it appears. */}
          {vm.errorMsg ? (
            <p role="alert" className="pb-wizard-footer-btn text-xs font-light text-error">
              {vm.errorMsg}
            </p>
          ) : null}
          {/* THE PRE-PRESS OUTSTANDING-ITEMS HINT (UP-F45). Lists what is still incomplete once the
              builder has tried to submit, in the same order onUpload re-checks its gates. It is a
              PROMPT, not a gate — the submit button below stays enabled, and pressing it focuses the
              first offender. Rendered only when there is something to list. Same faint ink and weight
              as the footer copy; the leading line uses the inactive text token so the bullets read as
              the actionable part. */}
          {outstanding.length > 0 ? (
            <div className="pb-wizard-footer-btn text-xs font-light text-ink">
              <p className="text-text-inactive">Before you can share this, finish:</p>
              <ul className="mt-1 list-disc pl-4">
                {outstanding.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {/* THE UPLOAD IN FLIGHT. Between pressing Publish and P5 appearing, the request actually
              uploads the artefact and re-runs the duplicate check, so this is real latency, not a
              staged pause — and until now the only sign of it was the button relabelling itself.

              WHY IT SITS HERE and not in the footer row below. That row is a measured
              `justify-between` with exactly two children, documented as such because the buttons hold
              their drawn inset off the modal's right edge; adding a third child, or widening the
              button group, moves the drawn Back button. This slot has no such constraint — it is
              where `vm.errorMsg` and the outstanding-items hint already come and go, so the column
              already tolerates content appearing and vanishing here, and it is the same spot the
              builder is looking for a verdict on the press they just made.

              Decorative, with no line of copy: the button below has already relabelled itself to
              "Publishing…"/"Submitting…" (see submitLabel), so a caption here would say it twice.

              WIDTH-CAPPED, and by a token rather than a number. The band's aspect is ~7.5:1, so at the
              full modal body width it would stand 134px tall — fine for P5's celebration, far too much
              for a busy indicator, because the footer group is `mt-auto` and every pixel it takes comes
              straight off the ScrollColumn holding the read-back. max-w-review-card is this frame's own
              measured preview-card width (445px), which puts the strip at ~60px: enough to read the bus
              and the streaks, small enough that the read-back barely moves. */}
          {vm.submitting ? (
            <div className="pb-wizard-footer-btn">
              <div className="max-w-review-card">
                <Kombi variant="band" />
              </div>
            </div>
          ) : null}
          {/* -mx-modal-x reaches both modal inner edges (canvas 190/1245). */}
          <div className="-mx-modal-x h-px bg-ink" />
          {/* THE FOOTER ROW — the attestation tick on the left, Back/Next on the
              right, all BELOW the rule on one line.

              The tick used to sit ABOVE the rule in its own full-width block. It is
              here now because the row is where the builder is already looking when
              they reach for Next, and the gate it controls is Next: the backend
              refuses to upload until this is ticked and stays on Review when it is
              not, so a dead Next button needs its reason within the same glance.
              Same paint as before, unchanged — 11px/300 ink, FormField's faint
              border on the box, the accent as its checked colour.

              `justify-between` replaces `justify-end`: with two children the label
              takes the left and the buttons keep the right inset they had. The
              buttons keep their own -mr-modal-x / pr-modal-footer-r / gap so their
              measured position off the modal's right edge is untouched. */}
          <div className="-mr-modal-x flex items-start justify-between gap-modal-footer pr-modal-footer-r pt-wizard-footer-btn">
            {/* `pr-*` keeps the copy off the buttons if it ever wraps; min-w-0 lets
                it shrink rather than push them out of position. */}
            <label
              className={[
                'flex min-w-0 items-start gap-3 pr-7 text-xs font-light',
                // The confirm copy reddens with the box when the gate is unmet.
                attestInvalid ? 'text-error' : 'text-ink',
              ].join(' ')}
            >
              <input
                type="checkbox"
                // `draft` is null until the journey creates one, exactly as the hook's
                // own `draft?.stage ?? 'details'` reads it. No draft means nothing has
                // been attested — which is also what `onUpload` concludes (it returns
                // early on a null draft), so the tick and the gate agree.
                checked={attested}
                onChange={(e) => vm.onAttestChange(e.target.checked)}
                {...(attestInvalid ? { 'aria-invalid': true } : {})}
                className={[
                  'mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm border accent-accent-link outline-none focus-visible:border-ring-focus',
                  // Red border until ticked, once the builder has tried to advance.
                  attestInvalid ? 'border-error' : 'border-border-subtle',
                ].join(' ')}
              />
              <span className="min-w-0">
                I confirm this write-up is accurate and contains no confidential or personal data.
              </span>
            </label>

            {/* The drawn pair, kept together so justify-between sees exactly two
                children and the buttons hold their measured right inset. */}
            <div className="flex shrink-0 items-start gap-modal-footer">
              {/* Returns to the previous wizard step (P3, Instructions). */}
              <Button
                variant="outline-square"
                size="xs"
                onClick={() => vm.goToStage('instructions')}
              >
                Back
              </Button>
              {/* Advances to wizard step 4 (final) — which on this surface means
                  DOING THE UPLOAD: P5 is reached by the upload succeeding
                  (`phase.kind === 'done'`), not by navigating to it. Disabled while
                  a request is in flight so a second click cannot double-submit. */}
              <Button
                variant="outline-square"
                size="xs"
                onClick={vm.onUpload}
                state={vm.submitting ? 'disabled' : 'default'}
              >
                {submitLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
